export type SsoProtocol = "SAML" | "OIDC";

/** SCIM is not a login protocol, so attribute sources are a wider set than SsoProtocol. */
export type IdentitySource = "SAML" | "OIDC" | "SCIM";

export interface IdentityAttributeMapping {
  email?: string[];
  firstName?: string[];
  lastName?: string[];
  displayName?: string[];
  phone?: string[];
}

export type IdentityAttributeKey = keyof IdentityAttributeMapping;

export const IDENTITY_ATTRIBUTE_FIELDS: {
  key: IdentityAttributeKey;
  label: string;
  helper: string;
}[] = [
  {
    key: "email",
    label: "Email",
    helper: "Used to find or create the user. Must look like an email address.",
  },
  {
    key: "displayName",
    label: "Display name",
    helper: "Takes precedence over first + last name.",
  },
  { key: "firstName", label: "First name", helper: "" },
  { key: "lastName", label: "Last name", helper: "" },
  {
    key: "phone",
    label: "Phone",
    helper: "Stored on the user's profile. Values over 25 characters are truncated.",
  },
];

export const SYNC_ELIGIBLE_ATTRIBUTE_FIELDS = IDENTITY_ATTRIBUTE_FIELDS.filter(
  (field) => field.key !== "email",
);

export type SourceMode = "default" | "custom";

export const MAX_ATTRIBUTE_PATH_LENGTH = 200;

export const MAX_ATTRIBUTE_MAPPING_BYTES = 4096;

export type AttributeMappingErrors = Partial<Record<IdentityAttributeKey, string>> & {

  form?: string;
};

export const validateAttributeMapping = (
  mapping: IdentityAttributeMapping,
  protocol: SsoProtocol,
  customFields: IdentityAttributeKey[],
  sourceMode: Partial<Record<IdentityAttributeKey, SourceMode>>,
  defaults: Partial<Record<IdentityAttributeKey, string[]>>,
): AttributeMappingErrors => {
  const errors: AttributeMappingErrors = {};

  customFields.forEach((key) => {
    if ((mapping[key] ?? []).length === 0) {
      errors[key] = "Add at least one attribute, or switch this field back to Default.";
    }
  });

  (Object.keys(mapping) as IdentityAttributeKey[]).forEach((key) => {
    if (errors[key]) return;
    const paths = mapping[key] ?? [];
    const seen = new Set<string>();

    for (const path of paths) {
      if (!path.trim()) {
        errors[key] = "Attribute names cannot be blank.";
        return;
      }
      if (path.length > MAX_ATTRIBUTE_PATH_LENGTH) {
        errors[key] = `Attribute names cannot be longer than ${MAX_ATTRIBUTE_PATH_LENGTH} characters.`;
        return;
      }

      if (seen.has(path)) {
        errors[key] = `"${path}" is listed more than once.`;
        return;
      }
      seen.add(path);

      if (protocol === "OIDC") {
        if (path.includes("[") || path.includes("]")) {
          errors[key] =
            `"${path}" uses filter/index syntax, which OIDC claim paths do not support. ` +
            "Use dotted paths such as address.locality.";
          return;
        }
        if (path.split(".").some((segment) => !segment.trim())) {
          errors[key] =
            `"${path}" has an empty segment. Use dotted paths such as address.locality.`;
          return;
        }
      }
    }
  });


  if (Object.keys(errors).length === 0) {
    const isFieldMapped = (key: IdentityAttributeKey): boolean =>
      (sourceMode[key] ?? "default") === "custom"
        ? (mapping[key] ?? []).length > 0
        : (defaults[key] ?? []).length > 0;

    const hasName =
      (isFieldMapped("firstName") && isFieldMapped("lastName")) || isFieldMapped("displayName");
    if (!hasName) {
      errors.form =
        "Map both First name and Last name, or map Display name — new users need one or the other.";
    }
  }

  if (Object.keys(errors).length === 0 && Object.keys(mapping).length > 0) {
    const bytes = new TextEncoder().encode(JSON.stringify(mapping)).length;
    if (bytes > MAX_ATTRIBUTE_MAPPING_BYTES) {
      errors.form = `This mapping is ${bytes} bytes, over the ${MAX_ATTRIBUTE_MAPPING_BYTES}-byte limit. Remove some attributes.`;
    }
  }

  return errors;
};

export type SsoStatus = "Draft" | "Active" | "Inactive";

export interface SsoRole {
  id: number;
  name: string;
}

export interface SsoFallbackUserCandidate {
  id: number;
  username?: string | null;
  name: string;
  email: string;
}

export interface SamlSp {
  entityId: string;
  acsUrl: string;
  metadataUrl: string;
  sloUrl: string;
}

export interface SamlConnectionDetail {
  metadataUrl?: string | null;
  idpEntityId?: string | null;
  idpSsoUrl?: string | null;
  idpVerificationCertificate?: string | null;
  idpSloUrl?: string | null;
  spEntityId?: string | null;
  spCertificate?: string | null;
  sendSubject?: boolean;
  sp?: SamlSp;
}

export interface OidcRp {
  redirectUri: string;
  postLogoutRedirectUri?: string;
  initiateUri?: string;
  sharedInitiateUri?: string;
}

export interface OidcConnectionDetail {
  issuer?: string | null;
  authorizationUri?: string | null;
  tokenUri?: string | null;
  userInfoUri?: string | null;
  jwkSetUri?: string | null;
  endSessionUri?: string | null;
  clientId?: string | null;
  clientAuthenticationMethod?: "client_secret_basic" | "client_secret_post" | null;
  scopes?: string[];
  usePkce?: boolean;
  acrValues?: string[] | null;
  prompt?: string | null;
  maxAge?: number | null;
  rp?: OidcRp;
}

export interface SsoConnection {
  id: number;
  account: string;
  app: string;
  protocol: SsoProtocol;
  slug: string;
  registrationId: string;
  emailDomains: string[];
  emailClaim?: string | null;
  attributeMapping?: IdentityAttributeMapping | null;

  profileSyncOnLogin?: boolean;
  /** Fields allowed to overwrite an existing user's profile on login. Never includes "email". */
  syncEnabledFields?: IdentityAttributeKey[];
  enforceSso: boolean;
  pwdFallbackUserId?: number | null;
  sendLoginHint: boolean;
  autoProvisionUsers: boolean;
  defaultRoleId?: number | null;
  status: SsoStatus;
  saml?: SamlConnectionDetail;
  oidc?: OidcConnectionDetail;
  createdOn?: string;
  updatedOn?: string;
}

export interface SsoReserveInput {
  protocol: SsoProtocol;
  slug?: string;
  emailDomains: string[];
  attributeMapping?: IdentityAttributeMapping | null;
  syncEnabledFields?: IdentityAttributeKey[];
  enforceSso?: boolean;
  pwdFallbackUserId?: number;
  sendLoginHint?: boolean;
  autoProvisionUsers?: boolean;
  defaultRoleId?: number;
}

export interface SamlConnectionDetailInput {
  metadataUrl?: string;
  idpEntityId?: string;
  idpSsoUrl?: string;
  idpVerificationCertificate?: string;
  idpSloUrl?: string;
  spEntityId?: string;
  spCertificate?: string;
  spPrivateKeyRef?: string;
  spPrivateKey?: string;
}

export interface OidcConnectionDetailInput {
  issuer?: string;
  authorizationUri?: string;
  tokenUri?: string;
  userInfoUri?: string;
  jwkSetUri?: string;
  endSessionUri?: string;
  clientId?: string;
  clientSecret?: string;
  clientAuthenticationMethod?: "client_secret_basic" | "client_secret_post";
  scopes?: string[];
  usePkce?: boolean;
  acrValues?: string[];
  prompt?: string;
  maxAge?: number;
}

export interface SsoConnectionInput {
  emailDomains: string[];
  emailClaim?: string;
  attributeMapping?: IdentityAttributeMapping | null;
  syncEnabledFields?: IdentityAttributeKey[];
  enforceSso?: boolean;
  pwdFallbackUserId?: number;
  sendLoginHint?: boolean;
  autoProvisionUsers?: boolean;
  defaultRoleId?: number;
  status?: SsoStatus;
  saml?: SamlConnectionDetailInput;
  oidc?: OidcConnectionDetailInput;
}
